<template>
  <header
    class="HeaderTitle mr-8 md min-w-0"
    :class="$mq"
    v-intersect="{ callback: onIntersect, options: { rootMargin: `${0}px` } }"
  >
    <div>
      <div class="flex space-x-8">
        <h1 class="text-base md:truncate md:pb-1" :class="!textSearch ? 'font-bold text-uppercase' : 'font-normal'">
          <template v-if="textSearch">
            Showing results for: <strong>{{ textSearch }}</strong>
          </template>
          <template v-else>{{ title }}</template>
        </h1>
      </div>
      <div>
        <div
          class="text-sm md:text-base text-grey-darker mt-2"
          :class="{
            invisible: loading || !countersVisible(),
            hidden: !term || countersHidden,
          }"
        >
          {{ countText }} | {{ partialText }}
        </div>
      </div>
    </div>

    <h5 class="xs:hidden sm:flex mt-2 text-md opacity-50" v-if="subtitle">
      {{ subtitle }}
    </h5>
    <div class="mt-2 text-base hidden sm:block" v-if="autoSuggest">
      <p>
        No matching results for: <em>{{ term }}</em>
      </p>
    </div>
  </header>
</template>

<script>
import { sizeToMetaId } from "@/lib/ListingFilters.js"
import isSearchPage from "@/lib/isSearchPage"
import { mapState } from "vuex"
import gql from "graphql-tag"

const CURRENT_USER_QUERY = gql`
  {
    currentUser @client {
      id
      admin
    }
  }
`

export default {
  name: "HeaderTitle",
  props: ["longtail", "competition"],
  apollo: {
    currentUser: {
      query: CURRENT_USER_QUERY,
    },
  },
  mounted() {
    this.$nextTick(() => this.setMeta())
  },
  updated() {
    this.setMeta()
  },
  methods: {
    isSearchPage,
    setMeta() {
      if (this.meta == null) return

      document.title = this.meta.title
      this.setMetaContent("description", this.meta.description)
      this.setMetaContent("keywords", this.meta.keyword)
    },
    setMetaContent(name, content) {
      document.querySelector(`meta[name="${name}"]`).setAttribute("content", content)
    },
    firstFilter(filterType) {
      return this[filterType].length == 1 ? this[filterType][0] : null
    },
    countersVisible() {
      return (this.exactMatches > 0 || this.partialCount > 0) && this.isSearchPage()
    },
    onIntersect(observer) {
      if (document.body.dataset.isMobile === "false") return
      if (!window.location.pathname.includes("/artworks/popular/")) return

      const rect = observer.entries?.[0]?.boundingClientRect
      const sideScroller = document.querySelector(".mobile-side-scroller")
      const mobileFilter = document.querySelector(".mobile-filter")
      const navbar = document.getElementById("main-navbar")

      if (rect.top === 0) return

      if (!observer.isIntersecting) {
        sideScroller.classList.add("hidden")
        mobileFilter.style.top = "100px"
      } else {
        sideScroller.classList.remove("hidden")
        mobileFilter.style.top = `${navbar.clientHeight - 2}px`
      }
    },
  },
  computed: {
    exactMatches() {
      return this.listingsCount - this.partialCount
    },
    countText() {
      return this.exactMatches <= 0 ? "No close match" : `${this.exactMatches} close matches`
    },
    partialText() {
      return `${this.partialCount > 1000 ? "1000+" : this.partialCount} partial matches`
    },
    countersHidden() {
      return !this.autoSuggest && !this.countersVisible() && this.subtitle
    },
    title() {
      let title

      switch (this.sort) {
        case "newest":
          title = "All"
          break
        case "popular":
          title = "Popular"
          break
        default:
          title = "All"
      }

      if (this.longtail.headline) {
        title = this.longtail.headline
      } else if (this.competition.categoryTitle) {
        title = `${this.competition.categoryTitle} Finalists`
      } else if (this.competition.name) {
        title = this.competition.name
      } else {
        if (this.longtail.keyword) {
          title = title + " " + this.longtail.keyword
        }
        title = title + " Art"
      }

      if (window.location.search.includes("title=")) {
        title = new URLSearchParams(window.location.search).get("title")
        return title
      }

      if (this.meta) title = this.meta.header
      if (this.featured) title = "staff picks"
      if (this.exclusive) title = "new exclusives"

      return title
    },
    subtitle() {
      if (this.exclusive) return "New artworks exclusive to Bluethumb"

      return this.longtail.content || (this.meta && this.meta.subheader)
    },
    textSearch() {
      return this.autoSuggest?.str || this.term
    },
    meta() {
      if (this.oneCollection) {
        return this.metas[this.firstFilter("collections")]["default"]
      } else if (this.oneArtStyle) {
        return this.metas[this.firstFilter("art_styles")]["default"]
      } else if (this.oneMedium) {
        return this.metas[this.firstFilter("mediums")]
      } else if (this.sizes.length == 1 && this.noCollectionsOrMediums) {
        return this.metas[sizeToMetaId(this.sizes[0])]
      }

      return null
    },
    oneCollection() {
      return this.firstFilter("collections") && !this.firstFilter("mediums")
    },
    oneArtStyle() {
      return !this.firstFilter("collections") && this.firstFilter("art_styles")
    },
    oneMedium() {
      return this.firstFilter("mediums") && !this.firstFilter("collections") && !this.firstFilter("art_styles")
    },
    noCollectionsOrMediums() {
      return this.mediums.length == 0 && this.collections.length == 0 && this.art_styles.length == 0
    },
    ...mapState("filter", {
      term: ({ filters }) => filters.term,
      autoSuggest: ({ autoSuggest }) => autoSuggest,
      sort: (state) => state.filters.sort,
      partialCount: (state) => state.partial_count,
      listingsCount: (state) => state.listings_count,
      loading: (state) => state.loading,
      featured: (state) => state.filters.featured,
      exclusive: (state) => state.filters.exclusive,
      collections: (state) => state.filters.collections,
      mediums: (state) => state.filters.mediums,
      art_styles: (state) => state.filters.art_styles,
      sizes: (state) => state.filters.sizes,
      metas: ({ config }) => config.meta,
      photography: ({ filters }) => {
        return filters.mediums.length == 1 && filters.mediums.includes("photograph")
      },
    }),
  },
}
</script>

<style lang="scss" scoped>
.HeaderTitle {
  h1 {
    font-size: 30px;
    line-height: 30px;
    min-width: 47%;
  }

  &.xs,
  &.sm {
    h1 {
      font-size: 19px;
      line-height: 26px;
    }
  }
}
</style>
